import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Card, ColorPicker, Flex, Typography } from "antd";
import { useTheme } from "../../../Context/ThemeContext";
import { BgColorsOutlined } from "@ant-design/icons";
import ThemeCard from "./ThemeCard";
import type { ColorPickerProps } from "antd";
import { generate, presetPalettes } from "@ant-design/colors";
import { themeConstants } from "../../../Utils/theme-constants";
import { Organization } from "../../../Entities/Organization";
import organizationService from "../../../Service/organization.service";
import { Theme } from "../../../Dto/Theme.dto";
import useAccess from "../../../Hooks/useAccess";
import SubscriptionWarning from "../../../Components/SubscriptionWarning";

type Presets = Required<ColorPickerProps>["presets"][number];
type Color = Extract<ColorPickerProps["value"], string | { cleared: any }>;

const genPresets = (presets = presetPalettes) =>
  Object.entries(presets).map<Presets>(([label, colors]) => ({
    label,
    colors,
  }));

const generateSecondaryColor = (primaryColor: string): string => {
  const presets = genPresets({
    primary: generate(primaryColor),
  });
  console.log("presets", presets);
  const firstColor = presets[0].colors[0];

  if (typeof firstColor === "string") {
    return firstColor;
  }
  console.warn("Unexpected color type:", typeof firstColor);
  return "#000000";
};

interface OrgDetailsProps {
  organizationDetails: Organization;
  setOrganizationDetails: any;
}

const cardStyle: React.CSSProperties = {
  padding: "16px",
  margin: "0",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  transition: "all 0.3s ease",
};

const gridStyle: React.CSSProperties = {
  textAlign: "center",
  borderRadius: "50%",
  width: "70px",
  height: "70px",
  overflow: "hidden",
  position: "relative",
  boxShadow: "none",
};

const ThemeDetails: React.FC<OrgDetailsProps> = ({
  organizationDetails,
  setOrganizationDetails,
}) => {
  const { currentLimits } = useAccess();
  const themes: Theme[] = themeConstants;
  const [open, setOpen] = useState<boolean>(false);

  const [color, setColor] = useState<Color>(
    organizationDetails?.theme.themeName === "Custom"
      ? organizationDetails.theme.primaryColor
      : "#1677ff",
  );
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const { applyCustomTheme } = useTheme();

  const bgColor = useMemo<string>(
    () => (typeof color === "string" ? color : color!.toHexString()),
    [color],
  );

  const btnStyle: React.CSSProperties = {
    color: bgColor,
  };

  const handleCustomTheme = (index: number) => {
    let newTheme;

    if (index < themes?.length) {
      const themeName = themes[index].themeName;
      if (
        currentLimits &&
        !currentLimits?.customBranding &&
        themeName !== "default" &&
        themeName
      ) {
        setOpen(true);
        return;
      }

      setSelectedCard(index);

      newTheme = {
        themeName: themes[index].themeName,
        primaryColor: themes[index].primaryColor,
        secondaryColor: themes[index].secondaryColor,
      };
    } else {
      if (currentLimits && !currentLimits?.customBranding) {
        setOpen(true);
        return;
      }
      setSelectedCard(index);
      const newSecondaryColor = generateSecondaryColor(bgColor);
      newTheme = {
        themeName: "Custom",
        primaryColor: bgColor,
        secondaryColor: newSecondaryColor,
      };
    }

    applyCustomTheme(newTheme);
    handleThemeUpdate(newTheme);
    localStorage.setItem("customTheme", JSON.stringify(newTheme));
  };

  const handleThemeUpdate = async (updatedTheme: any) => {
    if (
      updatedTheme.themeName === "default" &&
      !currentLimits?.customBranding
    ) {
      return;
    }
    try {
      await organizationService.changeTheme(updatedTheme);
      setOrganizationDetails((prevDetails: Organization) => ({
        ...prevDetails,
        theme: updatedTheme,
      }));
    } catch (error) {
      console.error("Error saving organization details:", error);
    }
  };

  useEffect(() => {
    if (selectedCard !== null) {
      fetchThemeByIndex(selectedCard);
    } else {
      fetchThemeByCode();
    }
  }, [selectedCard]);

  const fetchThemeByCode = useCallback(async () => {
    if (!organizationDetails?.theme) return;

    const themeIndex = themeConstants.findIndex(
      (theme) => theme.themeName === organizationDetails?.theme.themeName, // Match the theme by name
    );

    if (themeIndex !== -1) {
      setSelectedCard(themeIndex);
      applyCustomTheme({
        themeName: themeConstants[themeIndex].themeName,
        primaryColor: themeConstants[themeIndex].primaryColor,
        secondaryColor: themeConstants[themeIndex].secondaryColor,
      });
    } else {
      setSelectedCard(themeConstants?.length); // For custom theme
      applyCustomTheme(organizationDetails.theme); // Apply the custom theme
    }
  }, [organizationDetails?.theme, applyCustomTheme]);

  const fetchThemeByIndex = useCallback(
    (index: number) => {
      if (index < themes.length) {
        const theme = themes[index];
        applyCustomTheme({
          themeName: theme.themeName,
          primaryColor: theme.primaryColor,
          secondaryColor: theme.secondaryColor,
        });
      } else {
        const newSecondaryColor = generateSecondaryColor(bgColor);
        applyCustomTheme({
          themeName: "Custom",
          primaryColor: bgColor,
          secondaryColor: newSecondaryColor,
        });
      }
    },
    [themes, applyCustomTheme, bgColor],
  );

  return (
    <div className="theme-listing-section">
      {themes.map((theme, index) => (
        <Badge.Ribbon
          key={index}
          text={selectedCard === index ? "Current" : null}
          style={{ display: selectedCard === index ? "block" : "none" }}
        >
          <ThemeCard
            theme={theme}
            handleCardClick={() => handleCustomTheme(index)}
          />
        </Badge.Ribbon>
      ))}
      <Badge.Ribbon
        text={selectedCard === themes.length ? "Current" : null}
        style={{ display: selectedCard === themes.length ? "block" : "none" }}
      >
        <Card
          size="small"
          onClick={() => handleCustomTheme(themes.length)}
          hoverable
          style={{
            cursor: "pointer",
            ...cardStyle,
          }}
          className="theme-card"
        >
          <Flex
            align="center"
            justify="center"
            vertical
            style={{ width: "100%" }}
            gap="8px"
          >
            <Card.Grid style={gridStyle}>
              <ColorPicker
                value={color}
                onChange={setColor}
                style={{ width: "100%", height: "100%" }}
              >
                <Flex
                  align="center"
                  justify="center"
                  style={{ width: "100%", height: "100%" }}
                >
                  <BgColorsOutlined style={{ ...btnStyle, fontSize: "48px" }} />
                </Flex>
              </ColorPicker>
            </Card.Grid>
            <Typography.Title
              level={5}
              type="secondary"
              style={{
                textTransform: "capitalize",
                margin: 0,
                textAlign: "center",
              }}
            >
              Custom
            </Typography.Title>
          </Flex>
        </Card>
      </Badge.Ribbon>

      <SubscriptionWarning
        open={open}
        handleClose={() => setOpen(false)}
        title="Upgrade"
        type="custom-theme"
      />
    </div>
  );
};

export default ThemeDetails;
